import { Navigate, Route } from 'react-router-dom';
import { OrgPageNotFound, OrgScope, ShortFormRedirect } from '@/components/layouts/OrgScope';
import { CollegeSectionGate } from '@/features/college/components/CollegeSectionGate';
import { lazyPage } from './lazy';

const CollegeDashboardPage = lazyPage(
  () => import('@/features/college/pages/DashboardPage'),
  'CollegeDashboardPage'
);
const CollegePlacementsPage = lazyPage(
  () => import('@/features/college/pages/PlacementsPage'),
  'CollegePlacementsPage'
);
const CollegeStudentsPage = lazyPage(
  () => import('@/features/college/pages/StudentsPage'),
  'CollegeStudentsPage'
);
const PlacementDrivesPage = lazyPage(
  () => import('@/features/college/pages/PlacementDrivesPage'),
  'PlacementDrivesPage'
);
const CreateDrivePage = lazyPage(
  () => import('@/features/college/pages/PlacementDrivesPage'),
  'CreateDrivePage'
);
const CollegeCompaniesPage = lazyPage(
  () => import('@/features/college/pages/CollegeCompaniesPage'),
  'CollegeCompaniesPage'
);
const CollegeMessagesPage = lazyPage(
  () => import('@/features/college/pages/CollegeMessagesPage'),
  'CollegeMessagesPage'
);
const CollegeSettingsPage = lazyPage(
  () => import('@/features/college/pages/CollegeSettingsPage'),
  'CollegeSettingsPage'
);
const CollegeBillingPage = lazyPage(
  () => import('@/features/college/pages/CollegeBillingPage'),
  'CollegeBillingPage'
);
const CollegeCheckoutSuccessPage = lazyPage(
  () => import('@/features/college/pages/CollegeCheckoutSuccessPage'),
  'CollegeCheckoutSuccessPage'
);

const StructurePage = lazyPage(
  () => import('@/features/college/pages/StructurePage'),
  'StructurePage'
);
const SetupWizardPage = lazyPage(
  () => import('@/features/college/pages/SetupWizardPage'),
  'SetupWizardPage'
);
const CareerProfilesPage = lazyPage(
  () => import('@/features/college/pages/CareerProfilesPage'),
  'CareerProfilesPage'
);
const CollegeReportsPage = lazyPage(
  () => import('@/features/college/pages/CollegeReportsPage'),
  'CollegeReportsPage'
);
const RecruiterContactsPage = lazyPage(
  () => import('@/features/college/pages/RecruiterContactsPage'),
  'RecruiterContactsPage'
);
const CollegeDocumentsPage = lazyPage(
  () => import('@/features/college/pages/CollegeDocumentsPage'),
  'CollegeDocumentsPage'
);
const NewDocumentRequestPage = lazyPage(
  () => import('@/features/college/pages/CollegeDocumentsPage'),
  'NewDocumentRequestPage'
);
const DocumentRequestPage = lazyPage(
  () => import('@/features/college/pages/CollegeDocumentsPage'),
  'DocumentRequestPage'
);
const CollegeResumeReviewsPage = lazyPage(
  () => import('@/features/college/pages/CollegeResumeReviewsPage'),
  'CollegeResumeReviewsPage'
);
const CollegeResumeReviewPage = lazyPage(
  () => import('@/features/college/pages/CollegeResumeReviewsPage'),
  'CollegeResumeReviewPage'
);
const CollegeAssessmentsPage = lazyPage(
  () => import('@/features/college/pages/CollegeAssessmentsPage'),
  'CollegeAssessmentsPage'
);
const AssignAssessmentPage = lazyPage(
  () => import('@/features/college/pages/CollegeAssessmentsPage'),
  'AssignAssessmentPage'
);
const AssessmentAssignmentPage = lazyPage(
  () => import('@/features/college/pages/CollegeAssessmentsPage'),
  'AssessmentAssignmentPage'
);
const RecordsPage = lazyPage(() => import('@/features/college/pages/RecordsPage'), 'RecordsPage');
const SkillInsightsPage = lazyPage(
  () => import('@/features/college/pages/SkillInsightsPage'),
  'SkillInsightsPage'
);
const DriveManagePage = lazyPage(
  () => import('@/features/college/pages/DriveManagePage'),
  'DriveManagePage'
);
const OptOutsPage = lazyPage(() => import('@/features/college/pages/OptOutsPage'), 'OptOutsPage');
const NoticeboardPage = lazyPage(
  () => import('@/features/college/pages/NoticeboardPage'),
  'NoticeboardPage'
);
const StaffPage = lazyPage(() => import('@/features/college/pages/StaffPage'), 'StaffPage');
const BatchPage = lazyPage(() => import('@/features/college/pages/BatchPage'), 'BatchPage');
const InviteStudentPage = lazyPage(
  () => import('@/features/college/pages/InviteStudentPage'),
  'InviteStudentPage'
);
const ImportStudentsPage = lazyPage(
  () => import('@/features/college/pages/ImportStudentsPage'),
  'ImportStudentsPage'
);
const NewNoticePage = lazyPage(
  () => import('@/features/college/pages/NewNoticePage'),
  'NewNoticePage'
);
const InviteStaffPage = lazyPage(
  () => import('@/features/college/pages/InviteStaffPage'),
  'InviteStaffPage'
);
const StaffBatchesPage = lazyPage(
  () => import('@/features/college/pages/StaffBatchesPage'),
  'StaffBatchesPage'
);

export const CollegeRoutes = () => [
  // No organization named at all: resolve one (lib/org-path.ts).
  <Route key="college-root" path="/college" element={<ShortFormRedirect ctx="college" />} />,
  // /college/<slug>/… — the URL picks the college (OrgScope); every page then
  // sits behind the role gate (lib/college-access.ts).
  <Route key="college-org" path="/college/:orgSlug" element={<OrgScope ctx="college" />}>
    <Route index element={<Navigate to="dashboard" replace />} />
    <Route element={<CollegeSectionGate />}>
      <Route path="dashboard" element={<CollegeDashboardPage />} />

      {/* People */}
      <Route path="students" element={<CollegeStudentsPage />} />
      <Route path="students/invite" element={<InviteStudentPage />} />
      <Route path="students/import" element={<ImportStudentsPage />} />
      <Route path="staff" element={<StaffPage />} />
      <Route path="staff/invite" element={<InviteStaffPage />} />
      <Route path="staff/:userId/batches" element={<StaffBatchesPage />} />
      <Route path="students/profiles" element={<CareerProfilesPage />} />
      <Route path="students/resume-reviews" element={<CollegeResumeReviewsPage />} />
      <Route path="students/resume-reviews/:reviewId" element={<CollegeResumeReviewPage />} />

      {/* Companies */}
      <Route path="companies" element={<CollegeCompaniesPage />} />
      <Route path="companies/recruiters" element={<RecruiterContactsPage />} />

      {/* Drives */}
      <Route path="drives" element={<PlacementDrivesPage />} />
      <Route path="drives/create" element={<CreateDrivePage />} />
      <Route path="drives/:driveId" element={<DriveManagePage />} />
      <Route path="structure" element={<StructurePage />} />
      <Route path="structure/batches/:batchId" element={<BatchPage />} />
      <Route path="setup" element={<SetupWizardPage />} />
      <Route path="records" element={<RecordsPage />} />
      <Route path="skill-insights" element={<SkillInsightsPage />} />
      <Route path="opt-outs" element={<OptOutsPage />} />
      <Route path="notices" element={<NoticeboardPage />} />
      <Route path="notices/new" element={<NewNoticePage />} />
      <Route path="drives/active" element={<PlacementDrivesPage />} />
      <Route path="drives/past" element={<PlacementDrivesPage />} />

      {/* Assessments */}
      <Route path="assessments" element={<CollegeAssessmentsPage />} />
      <Route path="assessments/assign" element={<AssignAssessmentPage />} />
      <Route path="assessments/:assignmentId" element={<AssessmentAssignmentPage />} />
      <Route path="assessments/reports" element={<CollegeReportsPage />} />

      {/* Messages, Analytics, Documents, Settings */}
      <Route path="messages" element={<CollegeMessagesPage />} />
      <Route path="analytics" element={<CollegePlacementsPage />} />
      <Route path="documents" element={<CollegeDocumentsPage />} />
      <Route path="documents/new" element={<NewDocumentRequestPage />} />
      <Route path="documents/:requestId" element={<DocumentRequestPage />} />
      <Route path="billing" element={<CollegeBillingPage />} />
      <Route path="checkout/success" element={<CollegeCheckoutSuccessPage />} />
      <Route path="settings" element={<CollegeSettingsPage />} />
    </Route>
    <Route path="*" element={<OrgPageNotFound ctx="college" />} />
  </Route>,
];
